import { useMutation, useQuery } from '@tanstack/react-query';

import Icon from 'shopper/components/Icon';
import Link from 'shopper/components/Link';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { wishlistQueryKeys } from 'lib/queryKeys';

import {
  deleteOfferKeyword,
  getOfferAlias,
  postAddOfferAlias,
} from 'services/wishlist';

import useQueryClient from './useQueryClient';
import useToast from './useToast';

const useAliasWishlist = (gaEventCategory, { gaEventLabel } = {}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: aliasWishlist } = useQuery({
    queryKey: wishlistQueryKeys.productAlias(),
    queryFn: async () => getOfferAlias(),
  });

  const getOfferKeywordId = (aliasId) =>
    aliasWishlist?.items.find((item) => item.aliasId === aliasId)?.keywordId;

  const onUndoAliasClick = ({ action, aliasId, undoMutation }) => {
    sendEvent({
      action,
      category: gaEventCategory,
      label: gaEventLabel,
    });

    undoMutation({
      aliasId,
      showToastOnSuccess: false,
    });
  };

  const { mutate: mutateAddAlias } = useMutation({
    mutationFn: ({ aliasId }) => postAddOfferAlias({ aliasId }),
    onError: () => {
      showToast({
        iconLeft: <Icon name="info-sign" />,
        type: 'danger',
        text: placeholder('toasts.errors.wishlist.couldNotAddKeyword'),
      });
    },
    onMutate: () => {
      sendEvent({
        action: 'alias_add_attempt',
        category: gaEventCategory,
        label: gaEventLabel,
      });
    },
    onSuccess: (
      keyword,
      { mutateRemoveAlias, aliasId, showToastOnSuccess = true }
    ) => {
      sendEvent({
        action: 'alias_added',
        category: gaEventCategory,
        label: gaEventLabel,
      });

      queryClient.setQueryData(wishlistQueryKeys.productAlias(), {
        ...aliasWishlist,
        items: [keyword, ...aliasWishlist.items],
      });

      if (showToastOnSuccess) {
        showToast({
          text: placeholder('toasts.successes.wishlist.keywordAdded'),
          type: 'action',
          link: (
            <Link
              onClick={() =>
                onUndoAliasClick({
                  action: 'undo_added_alias',
                  aliasId,
                  undoMutation: mutateRemoveAlias,
                })
              }
            >
              {placeholder('actions.undo')}
            </Link>
          ),
        });
      }
    },
  });

  const { mutate: mutateRemoveAlias } = useMutation({
    mutationFn: ({ aliasId }) =>
      deleteOfferKeyword({ keywordId: getOfferKeywordId(aliasId) }),
    onError: () => {
      showToast({
        text: placeholder('toasts.errors.wishlist.couldNotRemoveKeyword'),
        type: 'danger',
      });
    },
    onMutate: () => {
      sendEvent({
        action: 'alias_remove_attempt',
        category: gaEventCategory,
        label: gaEventLabel,
      });
    },
    onSuccess: (_, { aliasId, showToastOnSuccess = true }) => {
      sendEvent({
        action: 'alias_removed',
        category: gaEventCategory,
        label: gaEventLabel,
      });

      queryClient.setQueryData(wishlistQueryKeys.productAlias(), {
        ...aliasWishlist,
        items: aliasWishlist.items.filter((item) => item.aliasId !== aliasId),
      });

      if (showToastOnSuccess) {
        showToast({
          text: placeholder('toasts.successes.wishlist.keywordRemoved'),
          type: 'action',
          link: (
            <Link
              onClick={() =>
                onUndoAliasClick({
                  action: 'undo_removed_alias',
                  aliasId,
                  undoMutation: mutateAddAlias,
                })
              }
            >
              {placeholder('actions.undo')}
            </Link>
          ),
        });
      }
    },
  });

  return {
    addOfferWishlistAlias: ({ aliasId }) =>
      mutateAddAlias({ aliasId, mutateRemoveAlias }),
    removeOfferWishlistAlias: mutateRemoveAlias,
    aliasWishlist,
  };
};

export default useAliasWishlist;
